import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Building2 } from "lucide-react";

const fallbackImage =
  "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMGZlc3RpdmFsJTIwY3Jvd2R8ZW58MHx8fHwxNzg3MDAyNDUzfDA&ixlib=rb-4.1.0&q=85";

export function formatEventDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// The one shared "full" event date+time formatter — day, full month name, year,
// and the local time. `new Date(iso)` + toLocaleString() always converts a
// UTC-with-offset ISO string (how starts_at/ends_at are stored) to the
// browser's own local time automatically, so this already shows the correct
// Europe/Amsterdam wall-clock time for a Dutch visitor without any manual
// timezone math — the same correctness the backend's own _fmt_dt() needed a
// real fix for (it used to render the raw UTC hour instead of converting).
// Used everywhere an event's date+time needs to read the same across the
// ticket shop, the order/ticket confirmation page, and (once downloaded) the
// PDF ticket — never a second, slightly different formatting rule per page.
export function formatEventDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function priceFromCents(types) {
  if (!Array.isArray(types) || types.length === 0) return null;
  const active = types.filter((t) => t.active !== false);
  if (active.length === 0) return null;
  return Math.min(...active.map((t) => t.price_cents || 0));
}

export function isEventSoldOut(types) {
  if (!Array.isArray(types) || types.length === 0) return false;
  const active = types.filter((t) => t.active !== false);
  if (active.length === 0) return false;
  return active.every(
    (t) => t.capacity != null && (t.sold_count || 0) >= t.capacity,
  );
}

export function formatPrice(cents) {
  if (cents == null) return "—";
  return `€ ${(cents / 100).toFixed(2).replace(".", ",")}`;
}

export default function EventCard({ ev }) {
  const org = ev.organization || ev.organizations || {};
  const isTimeslotBooking = !!ev.timeslot_booking_enabled;
  const min = priceFromCents(ev.ticket_types);
  const soldOut = isEventSoldOut(ev.ticket_types);
  const to = `/events/${ev.slug || ev.id}`;

  return (
    <Link
      to={to}
      data-testid={`event-card-${ev.id}`}
      className="group block bg-svj-card border border-svj-border rounded-2xl overflow-hidden hover:border-svj-cyan/60 transition-colors"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-svj-border">
        <img
          src={ev.cover_image_url || fallbackImage}
          alt={ev.title}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 bg-white p-1 rounded-full shadow-md w-12 h-12 flex items-center justify-center overflow-hidden">
          {org.logo_url ? (
            <img src={org.logo_url} alt={org.name || ""} className="w-full h-full object-cover rounded-full" />
          ) : (
            <Building2 className="h-6 w-6 text-svj-purple" />
          )}
        </div>
        {soldOut && (
          <div
            data-testid={`event-soldout-${ev.id}`}
            className="absolute top-4 right-4 bg-black/60 backdrop-blur-md border border-svj-border text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
          >
            Uitverkocht
          </div>
        )}
        <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-md border border-svj-border text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          {formatEventDate(ev.starts_at)}
        </div>
      </div>
      <div className="p-5">
        <div className="text-xs uppercase tracking-widest text-slate-400 truncate">
          {org.name || "Organisatie"}
        </div>
        <h3 className="mt-1 text-lg font-bold text-white leading-snug line-clamp-2">{ev.title}</h3>
        {ev.location && (
          <div className="mt-2 text-sm text-slate-400 flex items-center gap-1.5">
            <MapPin className="h-4 w-4" />
            <span className="truncate">{ev.location}</span>
          </div>
        )}
        <div className="mt-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              {isTimeslotBooking ? "Prijs" : min === 0 ? "Toegang" : "Vanaf"}
            </div>
            <div className="text-xl font-extrabold text-svj-cyan">
              {isTimeslotBooking ? "Per afspraak" : min === 0 ? "Gratis" : formatPrice(min)}
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-white/90 group-hover:text-white">
            {soldOut ? "Bekijk" : "Naar tickets"}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
}
